import { useState } from 'react';
import {
  Alert, Badge, Button, Group, NumberInput, PasswordInput, Stack, Switch,
  Table, Text, TextInput, Select, CopyButton, Code, Divider, Box, ActionIcon, Tooltip, List,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconDeviceCctv, IconPlugConnected, IconRefresh, IconUsers, IconClockHour4,
  IconAlertTriangle, IconCheck, IconCopy, IconX, IconDownload, IconDeviceDesktop,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { BlockLoader } from '../components/BlockLoader';
import { SectionCard } from '../components/SectionCard';
import { QurilmalarKartochkasi } from '../components/QurilmalarKartochkasi';
import { formatDateTime } from '../lib/format';
import { confirmDelete } from '../lib/confirm';
import { useClientPaged } from '../hooks/useClientPaged';
import { useAuth } from '../contexts/AuthContext';
import { isTenantStaff } from '../lib/permissions';
import { TablePagination } from '../components/TablePagination';
import { AgentJadvali } from '../components/AgentJadvali';
import { useServerSoati } from '../hooks/useServerSoati';
import { AgentOynasi } from '../components/AgentOynasi';
import { MAKTAB_AGENT_YOLLARI, type AgentQator } from '../lib/agent';
import { SILENT_ERROR } from '../lib/queryMeta';

interface Terminal {
  id: string; name: string; host: string; port: number; username: string;
  enabled: boolean; attendanceEnabled: boolean;
  model?: string | null; serialNumber?: string | null; firmware?: string | null;
  lastSeenAt?: string | null; lastSyncAt?: string | null; lastError?: string | null;
  // Faqat boshqaruvga keladi — buxgalterga server `null` qaytaradi (routes/faceid.ts)
  agentKey: string | null; ingestKey: string | null;
  _count?: { people: number; events: number };
  /** Shu terminalga xizmat qilgan kompyuterlar (oxirgi 30 kun) — SATA Agent ilovasi yoki eski Node agent. */
  agents?: AgentQator[];
}
/**
 * `faceid-terminals` so'rovining holati — Agentlar kartochkasi uchun (agentlar shu ro'yxat ichida keladi):
 * `dataUpdatedAt` — "hozir" yangi ro'yxat kelishi bilan siljiydi (useServerSoati); `isError` — qayta so'rov
 * yiqilgan, belgilar o'rnida xato va "Qayta urinish" (`refetch`, `isFetching`).
 */
type RoyxatSorovi = Pick<UseQueryResult<Terminal[]>, 'dataUpdatedAt' | 'isError' | 'isFetching' | 'refetch'>;
interface Person {
  id: string; employeeNo: string; name: string; hasFace: boolean; syncedAt?: string | null;
  user?: { id: string; fullName: string; role: string } | null;
}
interface Task {
  id: string; kind: string; status: string; error?: string | null;
  createdAt: string; finishedAt?: string | null;
}

/**
 * Qurilmadagi ismga mos ERP xodimini TAKLIF qilish.
 *
 * Qurilmaga ismlar qo'lda kiritilgan: ba'zisi to'liq ("AZIZOV MUHAMMAD"),
 * ba'zisi qisqa ("Afzal"). Shuning uchun aniq tenglik yetarli emas — bo'laklar
 * bo'yicha tekshiramiz: qurilmadagi har bir so'z ERP ismida uchrasa, bu nomzod.
 *
 * ATAYLAB FAQAT TAKLIF: avtomatik bog'lamaymiz. Xato bog'lanish bir xodimning
 * davomatini boshqasiga yozib yuborardi va buni sezish qiyin bo'lardi.
 */
function taklif(
  p: { name: string },
  xodimlar: { id: string; fullName: string }[]
): { id: string; fullName: string } | null {
  const tozala = (s: string) => s.toLowerCase().replace(/[^a-zа-яo'g'\s]/gi, ' ').trim();
  const bolaklar = tozala(p.name).split(/\s+/).filter((x) => x.length > 2);
  if (!bolaklar.length) return null;
  const mos = xodimlar.filter((x) => {
    const t = tozala(x.fullName);
    return bolaklar.every((b) => t.includes(b));
  });
  // Bittadan ko'p nomzod bo'lsa taklif qilmaymiz — tanlovni admin qilsin.
  return mos.length === 1 ? mos[0] : null;
}

/** Terminal oxirgi 3 daqiqada aloqaga chiqqan bo'lsa — onlayn. */
const ONLAYN_MS = 3 * 60 * 1000;
const onlayn = (t: Terminal) => !!t.lastSeenAt && Date.now() - new Date(t.lastSeenAt).getTime() < ONLAYN_MS;

/**
 * FACE ID — yuz tanish terminali sahifasi.
 *
 * MUHIM TUSHUNCHA (sahifadagi matnlar ham shunga qurilgan): davomat yig'ilishi
 * uchun bu panel OCHIQ turishi shart emas. Terminal har o'tishni o'zi bulutga
 * yuboradi — ishchi 07:12 da o'tsa, yozuv 07:12 da keladi, panelni kim qachon
 * ochgani ahamiyatsiz. Panel faqat KO'RISH va BOSHQARISH uchun.
 *
 * Boshqaruv amallari (sinxronlash, qo'shish) esa teskari yo'nalish: buyruq
 * bulutdan ichki tarmoqqa kirishi kerak, shuning uchun ular navbatga yoziladi
 * va maktab ichidagi agent bajaradi.
 */
export default function FaceId() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  // Buxgalter bo'limni ko'radi va buyruq beradi, lekin terminalni qo'sha/sozlay olmaydi
  const boshqaruv = isTenantStaff(user);
  const [tanlangan, setTanlangan] = useState<string | null>(null);

  const { data, isLoading, dataUpdatedAt, isError, isFetching, refetch } = useQuery({
    queryKey: ['faceid-terminals'],
    queryFn: async () => (await api.get<Terminal[]>('/faceid/terminals')).data,
    refetchInterval: 30_000, // onlayn holati o'zidan yangilanib tursin
    // Fonda yangilanmaydi — qaytganda darhol yangilansin (agent belgilari eski ma'lumot bilan qizarmasin, useServerSoati)
    refetchOnWindowFocus: true,
    // Xato sahifaning o'zida ochiq ko'rsatiladi (birinchi yuklash — pastdagi Alert, qayta so'rov — Agentlar kartochkasi):
    // 30 s lik takrorlashda toast yog'ilmasin
    meta: SILENT_ERROR,
  });
  const terminals = data ?? [];

  const terminal = terminals.find((x) => x.id === tanlangan) ?? terminals[0] ?? null;

  if (isLoading) return <BlockLoader variant="card" />;

  return (
    <Stack>
      <PageHeader title={t('nav.faceid')} subtitle={t('faceid.subtitle')} />

      <Alert icon={<IconClockHour4 size={18} />} color="teal" variant="light">
        {t('faceid.autonomyNote')}
      </Alert>

      {/* Birinchi yuklash yiqildi — ro'yxat "bo'sh" EMAS, noma'lum: "terminal qo'shish" formasi chiqsa boshqaruv
          mavjud terminalni takror yaratib qo'yishi mumkin edi */}
      {isError && !data ? (
        <Stack align="center" gap="sm">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
            {t('faceid.loadError')}
          </Alert>
          <Button variant="light" loading={isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void refetch()}>
            {t('agent.qaytaUrinish')}
          </Button>
        </Stack>
      ) : terminals.length === 0 ? (
        boshqaruv ? (
          <TerminalForm onSaved={() => qc.invalidateQueries({ queryKey: ['faceid-terminals'] })} />
        ) : (
          <Alert color="gray" variant="light">{t('faceid.noTerminalAsk')}</Alert>
        )
      ) : (
        <>
          {terminals.length > 1 && (
            <Select
              label={t('faceid.chooseTerminal')}
              data={terminals.map((x) => ({ value: x.id, label: x.name }))}
              value={terminal?.id ?? null}
              onChange={setTanlangan}
              allowDeselect={false}
              w={280}
            />
          )}
          {terminal && <TerminalPanel terminal={terminal} royxat={{ dataUpdatedAt, isError, isFetching, refetch }} />}
          {/* Panelga kirish kompyuterlari — maktab bo'yicha (terminalga emas) */}
          <QurilmalarKartochkasi />
        </>
      )}
    </Stack>
  );
}

/* ─────────────── Terminal qo'shish / sozlash formasi ─────────────── */

function TerminalForm({ terminal, onSaved }: { terminal?: Terminal; onSaved: () => void }) {
  const { t } = useTranslation();
  const tahrir = !!terminal;

  const form = useForm({
    initialValues: {
      name: terminal?.name ?? t('faceid.defaultName'),
      host: terminal?.host ?? '',
      port: terminal?.port ?? 80,
      username: terminal?.username ?? 'admin',
      password: '',
    },
    validate: {
      name: (v) => (!v.trim() ? t('faceid.nameRequired') : null),
      // IP yoki domen — qat'iy shakl talab qilmaymiz, lekin bo'sh bo'lmasin
      host: (v) => (!v.trim() ? t('faceid.hostRequired') : null),
      // Tahrirlashda bo'sh parol = "tegmaslik", yaratishda esa majburiy
      password: (v) => (!tahrir && !v ? t('faceid.passwordRequired') : null),
    },
  });

  const mut = useMutation({
    mutationFn: (v: typeof form.values) =>
      tahrir
        ? api.patch(`/faceid/terminals/${terminal!.id}`, v)
        : api.post('/faceid/terminals', v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      form.setFieldValue('password', '');
      onSaved();
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  return (
    <SectionCard title={tahrir ? t('faceid.settings') : t('faceid.connectTitle')} icon={IconPlugConnected}>
      <form onSubmit={form.onSubmit((v) => mut.mutate(v))}>
        <Stack gap="sm">
          {!tahrir && <Text size="sm" c="dimmed">{t('faceid.connectHint')}</Text>}
          <Group grow align="flex-start">
            <TextInput label={t('faceid.name')} {...form.getInputProps('name')} />
            <TextInput label={t('faceid.host')} placeholder="192.168.1.130" {...form.getInputProps('host')} />
          </Group>
          <Group grow align="flex-start">
            <NumberInput label={t('faceid.port')} min={1} max={65535} {...form.getInputProps('port')} />
            <TextInput label={t('faceid.username')} {...form.getInputProps('username')} />
          </Group>
          <PasswordInput
            label={t('faceid.password')}
            description={tahrir ? t('faceid.passwordKeep') : undefined}
            autoComplete="new-password"
            {...form.getInputProps('password')}
          />
          <Group justify="flex-end">
            <Button type="submit" loading={mut.isPending}>{t('common.save')}</Button>
          </Group>
        </Stack>
      </form>
    </SectionCard>
  );
}

/* ─────────────── Holat, tugmalar, ro'yxatlar ─────────────── */

function TerminalPanel({ terminal, royxat }: { terminal: Terminal; royxat: RoyxatSorovi }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const boshqaruv = isTenantStaff(useAuth().user);
  const yangila = () => qc.invalidateQueries({ queryKey: ['faceid-terminals'] });

  const { data: people = [] } = useQuery({
    queryKey: ['faceid-people', terminal.id],
    queryFn: async () => (await api.get<Person[]>(`/faceid/terminals/${terminal.id}/people`)).data,
  });
  /* Qurilmada 50+ odam bo'ladi va ro'yxat TO'LIQ keladi (serverda sahifalash
     yo'q — "bog'lanmaganlar soni" butun to'plam bo'yicha hisoblanadi). Shuning
     uchun ko'rsatishni brauzerda bo'lamiz, boshqa jadvallardagidek. */
  const sahifa = useClientPaged(people, 'faceid-people');

  const { data: tasks = [] } = useQuery({
    queryKey: ['faceid-tasks', terminal.id],
    queryFn: async () => (await api.get<Task[]>(`/faceid/terminals/${terminal.id}/tasks`)).data,
    refetchInterval: 10_000, // vazifa bajarilishini kuzatib turish uchun
  });
  const kutayotgan = tasks.filter((k) => k.status === 'PENDING' || k.status === 'RUNNING').length;

  /* Bog'lash uchun ERP xodimlari. Ota-ona/o'quvchi CHIQMAYDI: davomat faqat
     xodimlar uchun yuritiladi (`WorkLog`), ro'yxat esa 500+ o'quvchi bilan
     ishlatib bo'lmaydigan holga kelardi. */
  const { data: xodimlar = [] } = useQuery({
    queryKey: ['faceid-staff-options'],
    queryFn: async () =>
      (await api.get<{ id: string; fullName: string; role: string }[]>('/staff', {
        params: { isActive: 'true' },
      })).data,
  });

  const bogla = useMutation({
    mutationFn: (v: { id: string; userId: string | null }) =>
      api.patch(`/faceid/people/${v.id}`, { userId: v.userId }),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('faceid.linked') });
      qc.invalidateQueries({ queryKey: ['faceid-people', terminal.id] });
    },
    onError: (e: unknown) =>
      notifications.show({
        color: 'red',
        message: (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? t('common.error'),
      }),
  });

  const toggle = useMutation({
    mutationFn: (v: Partial<Terminal>) => api.patch(`/faceid/terminals/${terminal.id}`, v),
    onSuccess: yangila,
  });
  const vazifa = useMutation({
    mutationFn: (kind: string) => api.post(`/faceid/terminals/${terminal.id}/tasks`, { kind }),
    onSuccess: (r) => {
      const allaqachon = (r.data as { allaqachon?: boolean })?.allaqachon;
      notifications.show({
        color: allaqachon ? 'yellow' : 'green',
        message: allaqachon ? t('faceid.taskAlready') : t('faceid.taskQueued'),
      });
      qc.invalidateQueries({ queryKey: ['faceid-tasks', terminal.id] });
    },
  });
  /* Navbatdagi vazifani BEKOR QILISH. Agentsiz navbat qotib qolardi va uni
     paneldan tozalashning yo'li yo'q edi: admin tugmani bosardi, "allaqachon
     navbatda" degan javob olardi va shu bilan tamom. */
  const vazifaBekor = useMutation({
    mutationFn: (taskId: string) => api.delete(`/faceid/terminals/${terminal.id}/tasks/${taskId}`),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('faceid.taskCancelled') });
      qc.invalidateQueries({ queryKey: ['faceid-tasks', terminal.id] });
    },
  });

  const ochir = useMutation({
    mutationFn: () => api.delete(`/faceid/terminals/${terminal.id}`),
    onSuccess: () => { notifications.show({ color: 'green', message: t('faceid.removed') }); yangila(); },
  });

  const bogliqsiz = people.filter((p) => !p.user).length;

  return (
    <Stack>
      {/* Holat */}
      <SectionCard title={t('faceid.status')} icon={IconDeviceCctv}>
        <Stack gap="sm">
          <Group gap="xs">
            <Badge color={onlayn(terminal) ? 'teal' : 'gray'} size="lg">
              {onlayn(terminal) ? t('faceid.online') : t('faceid.offline')}
            </Badge>
            <Text size="sm" ff="monospace">{terminal.host}:{terminal.port}</Text>
            {terminal.model && <Badge variant="light">{terminal.model}</Badge>}
            {terminal.firmware && <Text size="sm" c="dimmed">{terminal.firmware}</Text>}
          </Group>

          <Group gap="lg">
            <Box>
              <Text size="xs" c="dimmed">{t('faceid.lastSeen')}</Text>
              <Text size="sm">{terminal.lastSeenAt ? formatDateTime(terminal.lastSeenAt) : '—'}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t('faceid.lastSync')}</Text>
              <Text size="sm">{terminal.lastSyncAt ? formatDateTime(terminal.lastSyncAt) : '—'}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t('faceid.peopleCount')}</Text>
              <Text size="sm">{terminal._count?.people ?? 0}</Text>
            </Box>
            <Box>
              <Text size="xs" c="dimmed">{t('faceid.eventCount')}</Text>
              <Text size="sm">{terminal._count?.events ?? 0}</Text>
            </Box>
          </Group>

          {terminal.lastError && (
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
              {terminal.lastError}
            </Alert>
          )}

          {/* Navbat KO'RINIB TURSIN. Paneldan xodim yaratilganda u terminalga
              yozish ishi shu navbatga tushadi, lekin qurilma maktab ichki
              tarmog'ida — ishni faqat o'sha yerdagi agent bajara oladi. Buni
              ko'rsatmasak, admin "yaratdim, bo'ldi" deb o'ylab yurardi va xodim
              terminalda yo'qligi faqat davomat kelmaganda bilinardi. */}
          {kutayotgan > 0 && (
            <Alert variant="light" color="orange" icon={<IconClockHour4 size={16} />}>
              {t('faceid.queued', { count: kutayotgan })}
              {/* SABAB ko'rsatiladi: navbat qotganda birinchi savol "nega?"
                  bo'ladi va javob agentning oxirgi ishlagan vaqtida. */}
              {terminal.lastSeenAt && (
                <Text size="xs" c="dimmed" mt={4}>
                  {t('faceid.agentLastSeen', { time: formatDateTime(terminal.lastSeenAt) })}
                </Text>
              )}
            </Alert>
          )}

          <Divider />

          <Switch
            checked={terminal.enabled}
            onChange={(e) => toggle.mutate({ enabled: e.currentTarget.checked })}
            label={t('faceid.enabled')}
            description={t('faceid.enabledHint')}
          // Terminal sozlamasi (PATCH) — faqat boshqaruv; buxgalter holatni ko'radi
          disabled={toggle.isPending || !boshqaruv} />
          <Switch
            checked={terminal.attendanceEnabled}
            onChange={(e) => toggle.mutate({ attendanceEnabled: e.currentTarget.checked })}
            label={t('faceid.attendanceEnabled')}
            description={t('faceid.attendanceEnabledHint')}
          // Terminal sozlamasi (PATCH) — faqat boshqaruv; buxgalter holatni ko'radi
          disabled={toggle.isPending || !boshqaruv} />

          <Divider />

          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              loading={vazifa.isPending}
              onClick={() => vazifa.mutate('SYNC_PEOPLE')}
            >
              {t('faceid.syncPeople')}
            </Button>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => vazifa.mutate('SYNC_EVENTS')}
             loading={vazifa.isPending}>
              {t('faceid.syncEvents')}
            </Button>
            <Button variant="default" onClick={() => vazifa.mutate('PING')} loading={vazifa.isPending}>{t('faceid.ping')}</Button>
          </Group>

          {/* Eng muhim tugma: shundan keyin qurilma davomatni O'ZI yuboradi va
              maktabda hech narsa yoqiq turishi shart bo'lmaydi (agent ham). */}
          <Button
            variant="light"
            color="teal"
            leftSection={<IconPlugConnected size={16} />}
            onClick={() => vazifa.mutate('SET_HTTP_HOST')}
           loading={vazifa.isPending}>
            {t('faceid.setHttpHost')}
          </Button>
          <Text size="xs" c="dimmed">{t('faceid.setHttpHostHint')}</Text>
          <Text size="xs" c="dimmed">{t('faceid.syncHint')}</Text>
        </Stack>
      </SectionCard>

      <AgentlarKartochkasi terminal={terminal} royxat={royxat} />

      {/* Qurilmadagi odamlar */}
      <SectionCard title={t('faceid.peopleTitle')} icon={IconUsers} p={0}>
        {people.length === 0 ? (
          <Text size="sm" c="dimmed" p="md">{t('faceid.noPeople')}</Text>
        ) : (
          <>
            {bogliqsiz > 0 && (
              <Alert color="yellow" variant="light" m="md" icon={<IconAlertTriangle size={16} />}>
                {t('faceid.unlinkedWarn', { count: bogliqsiz })}
              </Alert>
            )}
            <Table.ScrollContainer minWidth={620}>
              <Table highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('faceid.employeeNo')}</Table.Th>
                    <Table.Th>{t('faceid.deviceName')}</Table.Th>
                    <Table.Th>{t('faceid.face')}</Table.Th>
                    <Table.Th>{t('faceid.erpUser')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sahifa.items.map((p) => (
                    <Table.Tr key={p.id}>
                      <Table.Td ff="monospace">{p.employeeNo}</Table.Td>
                      <Table.Td>{p.name}</Table.Td>
                      <Table.Td>
                        {p.hasFace
                          ? <Badge color="teal" variant="light" leftSection={<IconCheck size={12} />}>{t('faceid.faceYes')}</Badge>
                          : <Badge color="gray" variant="light">{t('faceid.faceNo')}</Badge>}
                      </Table.Td>
                      <Table.Td>
                        <Select
                          size="xs"
                          w={240}
                          searchable
                          clearable
                          placeholder={t('faceid.pickStaff')}
                          data={xodimlar.map((x) => ({ value: x.id, label: x.fullName }))}
                          value={p.user?.id ?? null}
                          onChange={(v) => bogla.mutate({ id: p.id, userId: v })}
                          /* Taklif: qurilmadagi ism ERP xodimi ismiga o'xshasa,
                             katakcha yashil hoshiya bilan belgilanadi — admin
                             52 qatorni ko'z bilan solishtirib chiqmasin. */
                          styles={taklif(p, xodimlar) && !p.user ? { input: { borderColor: 'var(--mantine-color-teal-5)' } } : undefined}
                        />
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <TablePagination
              page={sahifa.page}
              pages={sahifa.pages}
              total={sahifa.total}
              limit={sahifa.limit}
              onPage={sahifa.setPage}
              onLimit={sahifa.setLimit}
            />
          </>
        )}
      </SectionCard>

      {/* Vazifalar tarixi */}
      {tasks.length > 0 && (
        <SectionCard title={t('faceid.tasksTitle')} icon={IconClockHour4} p={0}>
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing="xs">
              <Table.Tbody>
                {tasks.slice(0, 8).map((k) => (
                  <Table.Tr key={k.id}>
                    <Table.Td><Code>{k.kind}</Code></Table.Td>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={k.status === 'DONE' ? 'teal' : k.status === 'FAILED' ? 'red' : 'blue'}
                      >
                        {k.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDateTime(k.createdAt)}</Text></Table.Td>
                    <Table.Td><Text size="xs" c="red">{k.error ?? ''}</Text></Table.Td>
                    <Table.Td w={40}>
                      {k.status === 'PENDING' || k.status === 'RUNNING' ? (
                        <Tooltip label={t('faceid.taskCancel')} withArrow>
                          <ActionIcon
                            variant="subtle" color="red" size="sm"
                            loading={vazifaBekor.isPending && vazifaBekor.variables === k.id}
                            onClick={() => vazifaBekor.mutate(k.id)}
                          >
                            <IconX size={14} />
                          </ActionIcon>
                        </Tooltip>
                      ) : null}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </SectionCard>
      )}

      {/* Sozlama + kalitlar — faqat boshqaruv (buxgalterga kalit kelmaydi) */}
      {boshqaruv && (
        <>
          <TerminalForm terminal={terminal} onSaved={yangila} />

          <SectionCard title={t('faceid.keysTitle')} icon={IconPlugConnected}>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">{t('faceid.keysHint')}</Text>
              <KalitQatori label={t('faceid.ingestUrl')} value={`${import.meta.env.VITE_API_URL || ''}/faceid/events?key=${terminal.ingestKey ?? ''}`} />
              <KalitQatori label={t('faceid.agentKey')} value={terminal.agentKey ?? ''} />
            </Stack>
          </SectionCard>

          <Group justify="flex-end">
            <Button
              variant="subtle"
              color="red"
              loading={ochir.isPending}
              onClick={() => confirmDelete(terminal.name, () => ochir.mutate())}
            >
              {t('faceid.remove')}
            </Button>
          </Group>
        </>
      )}
    </Stack>
  );
}

/* ─────────────── Agentlar (maktab kompyuterlari) ─────────────── */

/**
 * Qaysi kompyuterlarda agent bor va ular hozir ishlayaptimi + SATA Agent (Tauri) o'rnatuvchisi. Navbat qotganda birinchi
 * savol "qaysi kompyuterni yoqish kerak?" — javob shu jadvalda. Qator bosilsa yon oyna: holat, vazifalar, log va buyruqlar
 * (spec 2026-10-15 agent boshqaruv §6.2).
 *
 * O'rnatuvchida maktab kaliti YO'Q (ochiq, imzolangan) — tugma bo'limni ko'radigan hammaga; kompyuter maktabga ilova ichida
 * agent kaliti bilan ulanadi va o'sha kalit faqat boshqaruvga ko'rsatiladi (server buxgalterga `null` beradi).
 *
 * Ro'yxatning qayta so'rovi yiqilsa jadval o'rnida ochiq xato + "Qayta urinish" (superadmin "FACE ID agentlar"
 * sahifasi kabi): eski ro'yxat qolib, "hozir" siljiyversa ~2 daqiqada hamma kompyuter yolg'on "Aloqa yo'q"
 * bo'lardi. Toast yo'q (`SILENT_ERROR` — so'rov butun sahifaniki, birinchi yuklash xatosini sahifa o'zi ko'rsatadi).
 */
function AgentlarKartochkasi({ terminal, royxat }: { terminal: Terminal; royxat: RoyxatSorovi }) {
  const { t } = useTranslation();
  const boshqaruv = isTenantStaff(useAuth().user);
  const [ochiq, setOchiq] = useState<string | null>(null);
  const agentlar = terminal.agents ?? [];
  // Holat belgilari server soati bo'yicha (panel ochilgan kompyuter soati noto'g'ri bo'lishi mumkin); 15 s da va
  // yangi ro'yxat bilan qayta hisoblanadi
  const hozir = useServerSoati(royxat.dataUpdatedAt);

  return (
    <SectionCard title={t('faceid.agentsTitle')} icon={IconDeviceDesktop}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">{t('faceid.agentsHint')}</Text>

        {royxat.isError ? (
          <Stack align="center" gap="sm">
            <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />} title={t('common.error')} w="100%">
              {t('agent.royxatYuklanmadi')}
            </Alert>
            <Button variant="light" loading={royxat.isFetching} leftSection={<IconRefresh size={16} />} onClick={() => void royxat.refetch()}>
              {t('agent.qaytaUrinish')}
            </Button>
          </Stack>
        ) : agentlar.length === 0 ? (
          <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
            {t('faceid.agentsEmpty')}
          </Alert>
        ) : (
          <>
            <AgentJadvali agentlar={agentlar} hozir={hozir} onTanla={setOchiq} />
            <Text size="xs" c="dimmed">{t('agent.qatorniBosing')}</Text>
          </>
        )}

        <Divider />

        <Stack gap="sm" data-testid="agent-ornatish">
          <AgentIlovaTugmasi />
          <List type="ordered" size="sm" spacing={4}>
            <List.Item>{t('faceid.appStep1')}</List.Item>
            <List.Item>{t('faceid.appStep2')}</List.Item>
            <List.Item>{t('faceid.appStep3')}</List.Item>
          </List>
          {boshqaruv ? (
            <>
              <KalitQatori label={t('faceid.agentKey')} value={terminal.agentKey ?? ''} />
              <Text size="xs" c="orange">{t('faceid.agentKeyWarn')}</Text>
            </>
          ) : (
            <Text size="sm" c="dimmed">{t('faceid.appKeyStaffOnly')}</Text>
          )}
        </Stack>
      </Stack>
      <AgentOynasi agentId={ochiq} onClose={() => setOchiq(null)} yol={MAKTAB_AGENT_YOLLARI} buyruqBera={boshqaruv} />
    </SectionCard>
  );
}

/** SATA Agent o'rnatuvchisi qaysi platforma uchun (maktab kompyuterlari — Windows). */
const ILOVA_PLATFORMA = 'windows-x86_64';
/** Hali reliz yo'q (manifest yo'q) — server 404 qaytaradi; qayta so'rash befoyda. */
const ilovaChiqmagan = (e: unknown) => (e as { response?: { status?: number } })?.response?.status === 404;

/**
 * "SATA Agent (Windows) yuklab olish" — havola va versiya backend'dan (`GET /faceid/agent/app/latest/windows-x86_64`, ochiq;
 * reliz chiqishi bilan tugma yangi o'rnatuvchini beradi). Oddiy havola: brauzer faylni yuklab oladi (server
 * `Content-Disposition: attachment`), yangi oyna ochilmaydi. Reliz yo'q yoki xato — tugma o'rnida qisqa izoh (toastsiz).
 */
function AgentIlovaTugmasi() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['agent-ilova', ILOVA_PLATFORMA],
    queryFn: async () => (await api.get<{ version: string; url: string }>(`/faceid/agent/app/latest/${ILOVA_PLATFORMA}`)).data,
    staleTime: 5 * 60_000,
    retry: (marta, e) => !ilovaChiqmagan(e) && marta < 1,
    // Xato tugma o'rnida izoh bilan ko'rsatiladi — toast takrorlamasin
    meta: SILENT_ERROR,
  });

  if (isLoading) {
    return <Group><Button leftSection={<IconDownload size={16} />} loading>{t('faceid.appDownload')}</Button></Group>;
  }
  // Havola faqat http(s) — boshqa sxema (javascript: va h.k.) tugmaga tushmasin
  const ilova = !isError && data && /^https?:\/\//i.test(data.url) ? data : null;
  if (!ilova) {
    return ilovaChiqmagan(error) ? (
      <Text size="sm" c="dimmed">{t('faceid.appNotReleased')}</Text>
    ) : (
      <Group gap="xs">
        <Text size="sm" c="red">{t('faceid.appLinkError')}</Text>
        <Button size="compact-sm" variant="light" loading={isFetching} leftSection={<IconRefresh size={14} />} onClick={() => void refetch()}>
          {t('agent.qaytaUrinish')}
        </Button>
      </Group>
    );
  }
  return (
    <Group gap="sm">
      <Button component="a" href={ilova.url} download leftSection={<IconDownload size={16} />}>
        {t('faceid.appDownload')}
      </Button>
      <Text size="sm" c="dimmed">{t('faceid.appVersion', { v: ilova.version })}</Text>
    </Group>
  );
}

/** Kalit qatori — nusxa olish tugmasi bilan (qo'lda ko'chirishda xato bo'lmasin). */
function KalitQatori({ label, value }: { label: string; value: string }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="xs" c="dimmed" w={120} style={{ flex: 'none' }}>{label}</Text>
      <Code style={{ flex: 1, overflowX: 'auto', whiteSpace: 'nowrap' }}>{value}</Code>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Button size="compact-xs" variant="light" color={copied ? 'teal' : 'gray'} onClick={copy}
            leftSection={copied ? <IconCheck size={12} /> : <IconCopy size={12} />}>
            {copied ? '✓' : ''}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}
